import { StorageType } from "../types";
import { log, shouldLogByProbability } from "../utils/logs";

export const safeLocalStorage = safeStorage(StorageType.Local);
export const safeSessionStorage = safeStorage(StorageType.Session);

/**
 * A safe wrapper around localStorage or sessionStorage to prevent errors
 * caused by browser restrictions (e.g., Safari private mode) or quota issues.
 *
 * - Handles errors automatically and prevents script crashes.
 * - Logs storage errors for debugging purposes.
 * - Fallback to default values on error to ensure continued execution.
 */
function safeStorage(type: StorageType) {
  const storage = type === StorageType.Local ? localStorage : sessionStorage;
  const handleError = <T>(action: () => T, fallback: T): T => {
    try {
      return action();
    } catch (error) {
      if (shouldLogByProbability()) {
        log(
          "rlink.storageError",
          {
            error: error instanceof Error ? error.message : error,
          },
          { severity: 4 },
        );
      }
      return fallback;
    }
  };

  return {
    get: (key: string): string | undefined =>
      handleError(() => storage.getItem(key), undefined),
    set: (key: string, value: string): void =>
      handleError(() => storage.setItem(key, value), void 0),
    remove: (key: string): void =>
      handleError(() => storage.removeItem(key), void 0),
  };
}
